// Supabase-based view tracking system
import { supabase } from '../lib/supabase';
import { caches, createCacheKey } from './queryCache';

// Get or create anonymous ID (stored in cookie)
const getAnonymousId = () => {
  let anonId = localStorage.getItem('outsera_anon_id');
  if (!anonId) {
    anonId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('outsera_anon_id', anonId);
  }
  return anonId;
};

/**
 * Track a view for a piece using Supabase Edge Function
 * @param {string} pieceId - The piece ID to track
 * @param {string|null} viewerWallet - The viewer's wallet address (optional)
 * @returns {Promise<{success: boolean, stats?: object, error?: string}>}
 */
export const trackPieceView = async (pieceId, viewerWallet = null) => {
  try {
    const anonId = getAnonymousId();
    
    console.log('📊 Tracking view:', {
      pieceId,
      viewerWallet: viewerWallet || 'anonymous',
      anonId: anonId.substring(0, 20) + '...',
    });

    // Call Supabase Edge Function
    const { data, error } = await supabase.functions.invoke('track-view', {
      body: {
        piece_id: pieceId,
        viewer_wallet: viewerWallet,
        anon_id: anonId,
      },
    });

    if (error) {
      // Check if it's a 429 (cooldown) error - this is normal, not an error
      if (error.message?.includes('429') || error.message?.includes('Too Many Requests')) {
        console.log('⏰ View cooldown active (already viewed within 30 minutes)');
        return { 
          success: false, 
          error: 'Cooldown active (already viewed within 30 minutes)' 
        };
      }
      
      console.error('❌ View tracking error:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to track view' 
      };
    }

    if (data.success) {
      console.log('✅ View tracked successfully:', data.stats);
      return {
        success: true,
        stats: data.stats,
      };
    } else {
      console.log('⏭️ View not tracked:', data.message);
      return {
        success: false,
        error: data.message || 'View not recorded (cooldown)',
      };
    }
  } catch (error) {
    console.error('❌ Unexpected error tracking view:', error);
    return {
      success: false,
      error: error.message || 'Unexpected error',
    };
  }
};

/**
 * Get trending pieces from Supabase
 * OPTIMIZED: Cached for 2 minutes to reduce database load
 * @param {number} limit - Number of pieces to return
 * @returns {Promise<Array>}
 */
export const getTrendingPieces = async (limit = 50) => {
  try {
    console.log('📈 Fetching trending pieces from Supabase...');

    // Use cache with 2 minute TTL for trending pieces
    const cacheKey = createCacheKey('trending-pieces', limit);
    const data = await caches.medium.getOrSet(cacheKey, async () => {
      // Query the piece_explore_scores view which has real-time trending scores
      const { data, error } = await supabase
        .from('piece_explore_scores')
        .select('*')
        .order('trending_score', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching trending pieces:', error);
        return [];
      }

      console.log(`✅ Fetched ${data.length} trending pieces from database`);
      return data || [];
    });

    return data;
  } catch (error) {
    console.error('Unexpected error fetching trending pieces:', error);
    return [];
  }
};

/**
 * Get view stats for a specific piece
 * @param {string} pieceId - The piece ID
 * @returns {Promise<object>}
 */
export const getPieceViewStats = async (pieceId) => {
  try {
    const { data, error } = await supabase
      .rpc('get_piece_view_stats', { p_piece_id: pieceId });

    if (error) {
      console.error('Error fetching view stats:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Unexpected error fetching view stats:', error);
    return null;
  }
};

/**
 * Get trending pieces using the materialized view (faster, but cached)
 * Refreshed every 5 minutes automatically
 * @param {number} limit - Number of pieces to return
 * @returns {Promise<Array>}
 */
export const getTrendingPiecesCached = async (limit = 50) => {
  try {
    console.log('📈 Fetching cached trending pieces from Supabase...');

    const { data, error } = await supabase
      .from('piece_trending_scores')
      .select('*')
      .order('trending_score', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching cached trending pieces:', error);
      return [];
    }

    console.log(`✅ Fetched ${data.length} cached trending pieces`);
    return data || [];
  } catch (error) {
    console.error('Unexpected error fetching cached trending pieces:', error);
    return [];
  }
};

/**
 * Hook to track view when piece detail modal opens
 * Call this in your openPieceDetailModal function
 */
export const useTrackPieceView = () => {
  return async (pieceId, walletAddress = null) => {
    return await trackPieceView(pieceId, walletAddress);
  };
};

